import type { IssueSuggestionItem } from '../extensions/issue-suggestion'
import { useEffect, useRef, useState } from 'react'

export interface IssueSuggestionPopupController {
  show: (items: IssueSuggestionItem[], clientRect: () => DOMRect | null, onSelect: (item: IssueSuggestionItem) => void) => void
  update: (items: IssueSuggestionItem[], clientRect: () => DOMRect | null) => void
  hide: () => void
  handleKeyDown: (event: KeyboardEvent) => boolean
}

let _instance: IssueSuggestionPopupController | null = null
// eslint-disable-next-line react-refresh/only-export-components -- imperative controller accessor read by Tiptap extensions outside the React tree
export function getIssueSuggestionPopup(): IssueSuggestionPopupController | null {
  return _instance
}

export function IssueSuggestionPopup() {
  const popoverRef = useRef<HTMLDivElement>(null)

  const [items, setItems] = useState<IssueSuggestionItem[]>([])
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [pos, setPos] = useState({ top: 0, left: 0 })
  const onSelectRef = useRef<((item: IssueSuggestionItem) => void) | null>(null)

  const itemsRef = useRef(items)
  itemsRef.current = items
  const selectedIndexRef = useRef(selectedIndex)
  selectedIndexRef.current = selectedIndex

  function applyRect(getRect: () => DOMRect | null) {
    const rect = getRect()
    if (rect)
      setPos({ top: rect.bottom, left: rect.left })
  }

  function selectItem(i: number) {
    const item = itemsRef.current[i]
    if (item && onSelectRef.current)
      onSelectRef.current(item)
  }

  useEffect(() => {
    _instance = {
      show(newItems, clientRect, onSelect) {
        setItems(newItems)
        setSelectedIndex(0)
        onSelectRef.current = onSelect
        applyRect(clientRect)
        popoverRef.current?.showPopover()
      },
      update(newItems, clientRect) {
        setItems(newItems)
        applyRect(clientRect)
      },
      hide() {
        popoverRef.current?.hidePopover()
        setItems([])
      },
      handleKeyDown(event: KeyboardEvent): boolean {
        if (event.key === 'ArrowDown') {
          setSelectedIndex(i => (i + 1) % Math.max(itemsRef.current.length, 1))
          return true
        }
        if (event.key === 'ArrowUp') {
          setSelectedIndex(i => (i - 1 + itemsRef.current.length) % Math.max(itemsRef.current.length, 1))
          return true
        }
        if (event.key === 'Enter') {
          selectItem(selectedIndexRef.current)
          return true
        }
        if (event.key === 'Escape') {
          popoverRef.current?.hidePopover()
          return true
        }
        return false
      },
    }

    return () => {
      _instance = null
    }
  }, [])

  return (
    <div
      ref={popoverRef}
      popover="manual"
      className="tiptap-suggestion-popup"
      style={{ position: 'fixed', top: `${pos.top}px`, left: `${pos.left}px` }}
    >
      <ul className="suggestion-list">
        {items.map((item, i) => (
          <li
            key={`${item.label}-${item.id}`}
            className={`suggestion-item${i === selectedIndex ? ' is-selected' : ''}`}
            onMouseDown={(e) => {
              e.preventDefault()
              selectItem(i)
            }}
            onMouseOver={() => setSelectedIndex(i)}
          >
            {item.label}
          </li>
        ))}
      </ul>
    </div>
  )
}
